import { fetchNextPage } from '@/components/SkuDetailPageRouter'
import { customerDashboardPath } from '@/lib/namedRoutes'
import { inPersonVisitRoutes } from '@/lib/routes/inPersonVisit'
import { urgentCareRoutes } from '@/lib/routes/urgentCare'
import { readScreeningQuestionnaireResponseId } from '@/lib/screeningQuestionnaireResponse'
import { clearUrgentCareOrderSession, readUrgentCareOrderIdFromSession } from '@/lib/urgentCare/bookingSession'
import { nextStepIdFromHash } from '@/lib/nextStepIdFromHash'

export const INSURANCE_VISIT_ID_SESSION_KEY = 'insuranceVisitId'
/** Cents for the SKU cash price shown on continue-without-insurance CTAs. */
export const INSURANCE_SKU_CASH_PRICE_CENTS_SESSION_KEY = 'insuranceSkuCashPriceCents'
const PRACTICE_ID_SESSION_KEY = 'insurancePracticeId'

function clearInsuranceSkuCashPriceCents() {
    sessionStorage.removeItem(INSURANCE_SKU_CASH_PRICE_CENTS_SESSION_KEY)
}

/** Look up a previously stashed cash amount. */
export function readInsuranceSkuCashPriceCents(): number | null {
    if (typeof window === 'undefined') return null
    const raw = sessionStorage.getItem(INSURANCE_SKU_CASH_PRICE_CENTS_SESSION_KEY)
    if (raw == null || raw === '') return null
    const cents = Number(raw)
    return Number.isFinite(cents) ? cents : null
}

/** Write SKU cash cents to sessionStorage (`null` clears). */
export function stashInsuranceSkuCashPriceCents(cashPriceCents: number | null) {
    if (typeof window === 'undefined') return
    if (cashPriceCents != null && Number.isFinite(cashPriceCents)) {
        sessionStorage.setItem(INSURANCE_SKU_CASH_PRICE_CENTS_SESSION_KEY, String(Math.round(cashPriceCents)))
    } else {
        clearInsuranceSkuCashPriceCents()
    }
}

export type RedirectFromInsuranceFlowResult = {
    url: string
    visitId: string | null
}

export const redirectFromInsuranceFlow = async (options?: {
    completedInsuranceOnboarding?: boolean
    visitId?: string
    /** Server-evaluated urgent care booking gate; defaults false (fail closed). */
    urgentCareBookingFlowEnabled?: boolean
}): Promise<RedirectFromInsuranceFlowResult> => {
    const referralOrderId = sessionStorage.getItem('referralOrderId')
    if (referralOrderId) {
        const practiceId = sessionStorage.getItem(PRACTICE_ID_SESSION_KEY)
        sessionStorage.removeItem('referralOrderId')
        sessionStorage.removeItem('insuranceReturnUrl')
        sessionStorage.removeItem('insuranceSkuId')
        sessionStorage.removeItem('insuranceSkuName')
        clearInsuranceSkuCashPriceCents()
        sessionStorage.removeItem('insuranceRefParam')
        sessionStorage.removeItem(PRACTICE_ID_SESSION_KEY)
        sessionStorage.removeItem('showContinueWithoutInsuranceButton')
        sessionStorage.removeItem('showTextUsSupport')
        sessionStorage.removeItem('insuranceFlowSource')
        sessionStorage.removeItem('insuranceSingleOptionFlow')
        sessionStorage.removeItem('isPriceGuaranteed')
        sessionStorage.removeItem('ssiFirstStepTracked')
        return {
            url: practiceId
                ? inPersonVisitRoutes.review(referralOrderId)
                : inPersonVisitRoutes.providers(referralOrderId),
            visitId: null,
        }
    }

    const urgentCareOrderId = readUrgentCareOrderIdFromSession()
    if (urgentCareOrderId && options?.urgentCareBookingFlowEnabled) {
        sessionStorage.removeItem('insuranceReturnUrl')
        sessionStorage.removeItem('insuranceSkuId')
        sessionStorage.removeItem('insuranceSkuName')
        clearInsuranceSkuCashPriceCents()
        sessionStorage.removeItem('insuranceRefParam')
        sessionStorage.removeItem(PRACTICE_ID_SESSION_KEY)
        sessionStorage.removeItem('showContinueWithoutInsuranceButton')
        sessionStorage.removeItem('showTextUsSupport')
        sessionStorage.removeItem('insuranceFlowSource')
        sessionStorage.removeItem('insuranceSingleOptionFlow')
        sessionStorage.removeItem('isPriceGuaranteed')
        sessionStorage.removeItem('ssiFirstStepTracked')
        sessionStorage.removeItem(INSURANCE_VISIT_ID_SESSION_KEY)
        clearUrgentCareOrderSession()
        return {
            url: urgentCareRoutes.options(urgentCareOrderId),
            visitId: null,
        }
    }
    if (urgentCareOrderId) {
        clearUrgentCareOrderSession()
    }

    const returnUrl = sessionStorage.getItem('insuranceReturnUrl')
    const hashFragment = sessionStorage.getItem('insuranceHashFragment')
    const renewalNextStepId = nextStepIdFromHash(hashFragment)
    const skuId = sessionStorage.getItem('insuranceSkuId') ?? ''
    const skuName = sessionStorage.getItem('insuranceSkuName') ?? ''
    let visitId =
        (options?.visitId?.trim() || null) ?? (sessionStorage.getItem(INSURANCE_VISIT_ID_SESSION_KEY)?.trim() || null)
    const goToConfirmOption = options?.completedInsuranceOnboarding === true

    // Check if we should use SkuDetailPageRouter to determine next step
    // This handles both explicit 'SkuDetailPageRouter' returnUrl AND cases where
    // returnUrl is missing but SKU context exists (fallback for lost sessionStorage)
    const shouldUseSkuRouter = returnUrl === 'SkuDetailPageRouter' || (skuId && skuName && !returnUrl)

    let redirectUrl: string
    if (shouldUseSkuRouter && skuId && skuName) {
        const refParam = sessionStorage.getItem('insuranceRefParam')
        const practiceId = sessionStorage.getItem(PRACTICE_ID_SESSION_KEY)
        const conversationSessionId =
            refParam === 'genai' && typeof window !== 'undefined'
                ? localStorage.getItem('genai_conversation_session_id')
                : null
        const result = await fetchNextPage({
            skuId,
            skuName,
            completedInsuranceSelfServe: true,
            conversationSessionId,
            preserveRefParam: refParam,
            practiceId,
            screeningQuestionnaireResponseId: readScreeningQuestionnaireResponseId(skuId),
            ...(renewalNextStepId && { nextStepId: renewalNextStepId }),
        })
        redirectUrl = result.url
        visitId ??= result.visitId ?? null
    } else {
        const isValidReturnUrl = returnUrl && returnUrl !== 'SkuDetailPageRouter'
        redirectUrl = isValidReturnUrl ? returnUrl : customerDashboardPath
    }

    const isPriceGuaranteed =
        typeof window !== 'undefined' ? sessionStorage.getItem('isPriceGuaranteed') === 'true' : false
    const usedSkuRouter = shouldUseSkuRouter && skuId && skuName
    const shouldRedirectToConfirmOption = goToConfirmOption && visitId && isPriceGuaranteed
    if (shouldRedirectToConfirmOption && visitId) {
        sessionStorage.setItem('insuranceReturnUrl', `/visit/${visitId}/schedule`)
        sessionStorage.setItem(INSURANCE_VISIT_ID_SESSION_KEY, visitId)
        redirectUrl = `/visit/${visitId}/options`
    } else if (visitId && !usedSkuRouter) {
        redirectUrl = `/visit/${visitId}/schedule`
    }

    sessionStorage.removeItem('insuranceRefParam')
    sessionStorage.removeItem(PRACTICE_ID_SESSION_KEY)
    sessionStorage.removeItem('showContinueWithoutInsuranceButton')
    sessionStorage.removeItem('showTextUsSupport')
    sessionStorage.removeItem('insuranceFlowSource')
    sessionStorage.removeItem('insuranceSingleOptionFlow')
    if (!shouldRedirectToConfirmOption) {
        sessionStorage.removeItem('insuranceReturnUrl')
        sessionStorage.removeItem('insuranceSkuId')
        sessionStorage.removeItem('insuranceSkuName')
        clearInsuranceSkuCashPriceCents()
        sessionStorage.removeItem(INSURANCE_VISIT_ID_SESSION_KEY)
        sessionStorage.removeItem('isPriceGuaranteed')
        sessionStorage.removeItem('ssiFirstStepTracked')
    }

    // Restore the hash fragment if it exists
    if (hashFragment) {
        redirectUrl = `${redirectUrl}${redirectUrl.includes('#') ? '' : hashFragment}`
        sessionStorage.removeItem('insuranceHashFragment')
    }

    return { url: redirectUrl, visitId }
}

export const setStateForInsuranceFlow = (
    skuId: string,
    skuName: string,
    refParam?: string | null,
    hashFragment?: string | null,
    practiceId?: string | null,
) => {
    sessionStorage.setItem('insuranceSkuId', skuId)
    sessionStorage.setItem('insuranceSkuName', skuName)
    sessionStorage.setItem('insuranceReturnUrl', 'SkuDetailPageRouter')
    sessionStorage.setItem('showContinueWithoutInsuranceButton', 'true')

    if (refParam === 'genai') {
        sessionStorage.setItem('insuranceRefParam', 'genai')
    }

    if (practiceId) {
        sessionStorage.setItem(PRACTICE_ID_SESSION_KEY, practiceId)
    } else {
        sessionStorage.removeItem(PRACTICE_ID_SESSION_KEY)
    }

    sessionStorage.removeItem(INSURANCE_VISIT_ID_SESSION_KEY)
    sessionStorage.removeItem('ssiFirstStepTracked')

    // Prefer an explicit hash (e.g. renew page passes #nextStepId from the path);
    // otherwise preserve the current window hash for insurance round-trips.
    const hash = hashFragment ?? (typeof window !== 'undefined' ? window.location.hash : '')
    if (hash) {
        sessionStorage.setItem('insuranceHashFragment', hash)
    } else {
        sessionStorage.removeItem('insuranceHashFragment')
    }
}

export const setInsuranceReturnPath = (
    currentPath: string,
    refParam?: string | null,
    showContinueWithoutInsurance = false,
) => {
    sessionStorage.setItem('insuranceReturnUrl', currentPath)
    sessionStorage.setItem('showContinueWithoutInsuranceButton', String(showContinueWithoutInsurance))

    if (refParam === 'genai') {
        sessionStorage.setItem('insuranceRefParam', 'genai')
    }

    sessionStorage.removeItem(INSURANCE_VISIT_ID_SESSION_KEY)
    sessionStorage.removeItem('ssiFirstStepTracked')
}
